// Workspace activity log. Talks to the events API (DynamoDB-backed) when it's
// available and falls back to localStorage for plain local dev, mirroring the
// team.js pattern. Pages read via listEvents(); features call logEvent()
// fire-and-forget so instrumentation never blocks the UI.
//
// Identity is SERVER-stamped: every request carries the Cognito ID token and
// the events lambda writes Actor from the verified token, never from the body.
// The `actor` argument to logEvent() survives only for the local mirror (the
// Events page in plain local dev, where there is no token to verify).
//
// Nothing tracked is allowed to vanish quietly. A POST that cannot be made
// right now — no token because the session lapsed, the network is down, the
// server answered 401 or 5xx — goes into a small localStorage OUTBOX and is
// retried oldest-first on load, on `online`, after the next sign-in and after
// every successful POST. The Activity page reads eventOutboxSize() so the
// owner can see "N events from this browser not yet uploaded" instead of a
// number that is silently short.
import { DEFAULT_CATEGORY_PATH } from './categories.js'
import { getIdToken, isCognitoConfigured } from './cognito.js'

const STORAGE_KEY = 'revailosales.events'
const OUTBOX_KEY = 'revailosales.eventOutbox'
const LOCAL_LIMIT = 500
// The outbox is FIFO and capped: past the cap the OLDEST entry is dropped,
// because a browser that has been offline for 500 events is better served by
// keeping the recent ones than by refusing to record anything new.
const OUTBOX_LIMIT = 500
// The server pages at most 200 rows per call; asking for more is clamped.
const PAGE_LIMIT = 200
// "Your session expired" is raised at most this often from this module, and
// only after the token has failed twice in a row — a single refresh hiccup
// must not nag the rep.
const EXPIRED_NOTICE_MS = 10 * 60 * 1000
const EXPIRED_AFTER_FAILURES = 2

// Display metadata per event type; also the client-side whitelist (the lambda
// enforces the same set server-side). `to` is where clicking the notification
// takes you — the nearest page where the thing that happened can be acted on.
//
// `to` IS OPTIONAL, and the types below that have none are HISTORICAL ONLY —
// nothing emits them any more. The Outreach and Follow Up sections they pointed
// at are gone, but their rows are already written into the live activity log and
// history is never rewritten: the label and group stay so an old row still reads
// as words in the audit log and the bell, and only the dead link is dropped.
//
// A type the app STILL EMITS must keep a `to` that resolves. NotificationsMenu
// marks every row read and closes the menu before it checks `to`, so a live type
// with no destination turns the bell into a dead click that silently clears
// unread rows the user never opened. When the Outreach pages went, that caught
// outcome_logged, email_drafted and email_sent — the three commonest things a
// rep does — and they were re-pointed at the leads directory, which is where the
// lead behind each of them is worked.
export const EVENT_TYPES = {
  sign_in: { label: 'Signed in', group: 'account', to: '/logs/events' },
  region_viewed: { label: 'Viewed region', group: 'leads', to: DEFAULT_CATEGORY_PATH },
  leads_enriched: { label: 'Saved leads', group: 'leads', to: DEFAULT_CATEGORY_PATH },
  leads_ai_enriched: { label: 'AI enrichment finished', group: 'leads', to: DEFAULT_CATEGORY_PATH },
  sequence_created: { label: 'Created sequence', group: 'outreach' },
  sequence_updated: { label: 'Updated sequence', group: 'outreach' },
  sequence_deleted: { label: 'Deleted sequence', group: 'outreach' },
  sequence_started: { label: 'Started outreach', group: 'outreach' },
  sequence_discovered: { label: 'Found a sequence', group: 'outreach' },
  campaign_started: { label: 'Launched campaign', group: 'outreach' },
  // HISTORICAL ONLY. Nothing emits this any more — the audience-list feature
  // and its page are gone. The label stays so rows already in the audit log
  // keep rendering as words instead of falling back to the raw type string.
  list_created: { label: 'Built audience list', group: 'leads' },
  lead_added: { label: 'Added lead manually', group: 'leads', to: DEFAULT_CATEGORY_PATH },
  leads_imported: { label: 'Leads imported', group: 'leads', to: DEFAULT_CATEGORY_PATH },
  outcome_logged: { label: 'Logged outcome', group: 'outreach', to: DEFAULT_CATEGORY_PATH },
  member_role_changed: { label: 'Changed member role', group: 'team', to: '/collaboration' },
  member_password_changed: { label: 'Changed member password', group: 'team', to: '/collaboration' },
  email_drafted: { label: 'Drafted email with AI', group: 'outreach', to: DEFAULT_CATEGORY_PATH },
  member_invited: { label: 'Invited member', group: 'team', to: '/collaboration' },
  member_removed: { label: 'Removed member', group: 'team', to: '/collaboration' },
  gmail_connected: { label: 'Connected Gmail', group: 'integrations', to: '/settings' },
  gmail_disconnected: { label: 'Disconnected Gmail', group: 'integrations', to: '/settings' },
  email_sent: { label: 'Sent email', group: 'outreach', to: DEFAULT_CATEGORY_PATH },
  // Sent / scheduled through Apollo from a draft card or the composer. Never
  // counted as outreach: Apollo logs the email to HubSpot, which is counted.
  apollo_email_sent: { label: 'Sent email via Apollo', group: 'outreach', to: '/chat' },
  apollo_email_scheduled: { label: 'Scheduled email via Apollo', group: 'outreach', to: '/chat' },
  // The investor-facing routine: the three daily operations, the questions a
  // rep asks, and the drafts they actually take to HubSpot. Logged from
  // Chat.jsx with a structured `data` payload; the Activity page counts them.
  operation_started: { label: 'Started a daily operation', group: 'operations', to: '/chat' },
  operation_run: { label: 'Ran a daily operation', group: 'operations', to: '/chat' },
  question_asked: { label: 'Asked the assistant', group: 'chat', to: '/chat' },
  draft_copied: { label: 'Copied a draft', group: 'outreach', to: '/chat' },
  hubspot_email_opened: { label: 'Opened HubSpot to email', group: 'outreach', to: '/chat' },
  impersonate_start: { label: 'Signed in as a member', group: 'team', to: '/collaboration' },
  impersonate_stop: { label: 'Returned to own account', group: 'team', to: '/collaboration' },
}

// ── localStorage (the mirror and the outbox) ─────────────────────────────────
function readList(key) {
  try {
    const raw = localStorage.getItem(key)
    // A malformed store must not crash a render: anything but the
    // expected shape is treated as empty.
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function readLocal() {
  return readList(STORAGE_KEY)
}

function writeLocal(events) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(events.slice(0, LOCAL_LIMIT)))
}

function readOutbox() {
  return readList(OUTBOX_KEY)
}

function writeOutbox(entries) {
  try {
    // Oldest first in storage; keep the NEWEST when over the cap.
    const kept = entries.length > OUTBOX_LIMIT ? entries.slice(entries.length - OUTBOX_LIMIT) : entries
    localStorage.setItem(OUTBOX_KEY, JSON.stringify(kept))
  } catch {
    /* quota / private mode — the mirror still has the row */
  }
}

// How many tracked events this browser has not managed to upload yet. The
// Activity page shows it so a short number is never mistaken for a true one.
export function eventOutboxSize() {
  return readOutbox().length
}

// ── HTTP plumbing ────────────────────────────────────────────────────────────
async function apiJson(res) {
  if (!res.ok) return null
  const ct = res.headers.get('content-type') || ''
  if (!ct.includes('application/json')) return null
  return res.json()
}

// The Authorization header for this request, or none. In a build without
// Cognito there is nothing to sign with and the caller goes straight to the
// local mirror; with Cognito an empty token means the session has lapsed.
async function bearer() {
  if (!isCognitoConfigured) return ''
  try {
    return (await getIdToken()) || ''
  } catch {
    return ''
  }
}

function authHeaders(token, extra = {}) {
  return token ? { ...extra, Authorization: `Bearer ${token}` } : extra
}

// `since` / `until` may be ISO instants or 'YYYY-MM-DD'; the server normalises
// both. `types` may be a string or an array — the server takes a comma list.
function buildQuery({ limit, cursor, since, until, actor, types, all, summary } = {}) {
  const qs = new URLSearchParams()
  if (limit) qs.set('limit', String(Math.max(1, Math.min(PAGE_LIMIT, Number(limit) || PAGE_LIMIT))))
  if (cursor) qs.set('cursor', cursor)
  if (since) qs.set('since', String(since))
  if (until) qs.set('until', String(until))
  if (actor) qs.set('actor', String(actor).trim().toLowerCase())
  const typeList = Array.isArray(types) ? types : types ? String(types).split(',') : []
  const cleaned = typeList.map((t) => String(t || '').trim()).filter(Boolean)
  if (cleaned.length) qs.set('type', cleaned.join(','))
  if (all) qs.set('all', '1')
  if (summary) qs.set('summary', '1')
  return qs
}

// One GET against the events API. Resolves { data, status }: `data` is the
// parsed JSON body on a 2xx JSON answer and null otherwise; `status` is the
// HTTP status, or 0 when the request never got an answer (offline, CORS, a
// thrown fetch). Never throws — every caller needs the status to explain
// itself ("sign in again" vs "not a member" vs "unavailable").
async function getEvents(params, token) {
  try {
    const res = await fetch(`/api/events?${buildQuery(params)}`, { headers: authHeaders(token) })
    const data = await apiJson(res)
    return { data, status: res.status || 0 }
  } catch {
    return { data: null, status: 0 }
  }
}

// Server PascalCase → client camelCase. `data` is the structured payload the
// new event types carry (an object) or null — passed through untouched so the
// page reads exactly what was stored.
function normalize(e) {
  const data = e.Data !== undefined ? e.Data : e.data
  return {
    id: e.Id || e.id,
    type: e.Type || e.type,
    actor: e.Actor || e.actor || '',
    actorName: e.ActorName || e.actorName || '',
    detail: e.Detail || e.detail || '',
    data: data && typeof data === 'object' && !Array.isArray(data) ? data : null,
    createdAt: e.CreatedAt || e.createdAt || '',
  }
}

export { normalize }

// ── reads ────────────────────────────────────────────────────────────────────
// Returns { events, cursor, mode, status }. Pass the previous call's cursor to
// page further back; cursor is null once the log is exhausted. `mode` is 'api'
// when the rows came from the server and 'local' when they are this browser's
// mirror; `status` is the HTTP status that caused a fallback (0 = no answer)
// so a caller can tell 401 ("sign in again") and 403 ("not a member") from an
// outage. Without Cognito configured there is no token to send, so the call
// goes straight to the mirror.
export async function listEvents({ limit = 100, cursor = null, since, until, actor, types } = {}) {
  let status = 0
  if (isCognitoConfigured) {
    const token = await bearer()
    const got = await getEvents({ limit, cursor, since, until, actor, types }, token)
    status = got.status
    if (got.data && Array.isArray(got.data.events)) {
      return { events: got.data.events.map(normalize), cursor: got.data.cursor || null, mode: 'api', status }
    }
  }
  // On a paginated "load older" call the caller already has page 1; returning
  // the whole local mirror here would append duplicates (every event is also
  // mirrored locally). Only seed the local feed on the first page.
  if (cursor) return { events: [], cursor: null, mode: 'local', status }
  return { events: readLocal().map(normalize), cursor: null, mode: 'local', status }
}

// This browser's own mirror, normalised, newest first, with no network. The
// Events page falls back to it when the server cannot answer (clipped to its
// window and labelled as this browser's copy); in a build without Cognito it is
// the whole record.
export function localEvents() {
  return readLocal().map(normalize)
}

// Every row in a range, following the server's cursor until the range is
// exhausted or `maxMs` has passed. Resolves { events, complete, mode, status }.
// `complete` is false whenever the walk stopped early (time, or a page that
// failed) — the page must say so rather than show a short count. This NEVER
// falls back to the local mirror: an investor-facing number must come from
// the server or not at all, so an unreachable API yields events:[] with
// mode:'local' and the status that explains it.
export async function listEventsRange({ since, until, actor, types, maxMs = 20000 } = {}) {
  if (!isCognitoConfigured) return { events: [], complete: false, mode: 'local', status: 0 }
  const token = await bearer()
  const started = Date.now()
  const events = []
  let cursor = null
  let pages = 0
  for (;;) {
    const got = await getEvents({ limit: PAGE_LIMIT, cursor, since, until, actor, types, all: true }, token)
    if (!got.data || !Array.isArray(got.data.events)) {
      // First page failed: nothing trustworthy to hand back. A later page
      // failed: what was read is real, but the range is not finished.
      if (!pages) return { events: [], complete: false, mode: 'local', status: got.status }
      return { events, complete: false, mode: 'api', status: got.status }
    }
    pages += 1
    for (const e of got.data.events) events.push(normalize(e))
    cursor = got.data.cursor || null
    if (!cursor) return { events, complete: true, mode: 'api', status: got.status }
    if (Date.now() - started >= maxMs) return { events, complete: false, mode: 'api', status: got.status }
  }
}

// Per-day counts per type over a range, computed by the server over EVERY row
// in the range (no page cap). Resolves { days, total, first_at, last_at, tz,
// complete, mode, status }. No local fallback: when the API cannot answer,
// days is {} and total 0 with mode:'local' and complete:false — a caller must
// treat that as "unknown", never as zero. Impersonated rows come back under
// '<type>@impersonated' keys so a member's numbers can exclude them.
export async function eventsSummary({ since, until, actor, types } = {}) {
  const empty = { days: {}, total: 0, first_at: null, last_at: null, tz: 'America/Chicago', complete: false }
  if (!isCognitoConfigured) return { ...empty, mode: 'local', status: 0 }
  const token = await bearer()
  const got = await getEvents({ since, until, actor, types, summary: true }, token)
  const d = got.data
  if (!d || !d.days || typeof d.days !== 'object') return { ...empty, mode: 'local', status: got.status }
  return {
    days: d.days,
    total: Number(d.total) || 0,
    first_at: d.first_at || null,
    last_at: d.last_at || null,
    tz: d.tz || 'America/Chicago',
    complete: d.complete === true,
    scanned: Number(d.scanned) || 0,
    mode: 'api',
    status: got.status,
  }
}

// ── writes ───────────────────────────────────────────────────────────────────
// The session has stopped producing a token. Shell listens for `auth:expired`
// and shows the one "sign in again" toast (the same event requireIdToken
// fires). Throttled: only after repeated failures, and at most once per
// EXPIRED_NOTICE_MS, so a queue of 50 retries does not raise 50 toasts.
let authFailures = 0
let lastExpiredNoticeAt = 0
function noteAuthFailure() {
  authFailures += 1
  if (authFailures < EXPIRED_AFTER_FAILURES) return
  const now = Date.now()
  if (now - lastExpiredNoticeAt < EXPIRED_NOTICE_MS) return
  lastExpiredNoticeAt = now
  try {
    window.dispatchEvent(new CustomEvent('auth:expired'))
  } catch {
    /* non-browser context */
  }
}
function noteAuthOk() {
  authFailures = 0
}

// One POST. Resolves { ok, status, retry }: `retry` says whether the outbox
// should hold on to the event (no answer, 401, 408, 429, 5xx) or let it go
// (2xx delivered; any other 4xx is the server refusing the event itself —
// an unknown type, a bad body — which no retry will change).
async function postEvent(body, token) {
  try {
    const res = await fetch('/api/events', {
      method: 'POST',
      headers: authHeaders(token, { 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    })
    const status = res.status || 0
    if (res.ok) return { ok: true, status, retry: false }
    const retry = status === 401 || status === 408 || status === 429 || status >= 500
    return { ok: false, status, retry }
  } catch {
    return { ok: false, status: 0, retry: true }
  }
}

// The wire body for an outbox entry. `clientAt` rides inside `data` so the
// server's CreatedAt (the upload time) never silently replaces the moment the
// thing actually happened — a flush after a weekend offline must not move
// Friday's work to Monday.
function bodyFor(entry) {
  const data = entry.data && typeof entry.data === 'object' ? entry.data : null
  return {
    type: entry.type,
    actor_name: entry.actor_name || '',
    detail: entry.detail || '',
    data: data ? { ...data, clientAt: entry.at } : { clientAt: entry.at },
  }
}

function removeFromOutbox(id) {
  const cur = readOutbox()
  const next = cur.filter((e) => !(e && e.id === id))
  if (next.length !== cur.length) writeOutbox(next)
}

function enqueue(entry) {
  const box = readOutbox()
  // Dedupe by local id: a flush that raced a re-enqueue must not double-post.
  if (box.some((e) => e && e.id === entry.id)) return
  box.push(entry)
  writeOutbox(box)
}

// Drain the outbox oldest-first, stopping at the first failure so order is
// kept and a dead server is hit once, not once per entry. Re-entrant calls
// share the in-flight drain. Resolves { sent, remaining, status }.
let flushing = null
async function drainOutbox() {
  let sent = 0
  let status = 0
  try {
    if (!isCognitoConfigured) return { sent, remaining: readOutbox().length, status }
    let box = readOutbox()
    if (!box.length) return { sent, remaining: 0, status }
    const token = await bearer()
    if (!token) {
      noteAuthFailure()
      return { sent, remaining: box.length, status: 401 }
    }
    while (box.length) {
      const entry = box[0]
      const res = await postEvent(bodyFor(entry), token)
      status = res.status
      if (res.ok || !res.retry) {
        // Delivered, or refused for what it is (the server will never take
        // it; keep the rest). Either way the entry leaves the box — but BY
        // ID against a fresh read, never by writing back the snapshot this
        // loop started with: logEvent() enqueues while a POST is in flight
        // (a 5xx, a dead network, a lapsed token — exactly when the box is
        // in use), and a stale `box.slice(1)` would overwrite that entry
        // out of existence. Nothing tracked may vanish quietly.
        if (res.ok) {
          noteAuthOk()
          sent += 1
        }
        removeFromOutbox(entry.id)
        box = readOutbox()
        continue
      }
      if (res.status === 401) noteAuthFailure()
      break
    }
    return { sent, remaining: readOutbox().length, status }
  } catch {
    return { sent, remaining: readOutbox().length, status }
  }
}
export function flushEventOutbox() {
  if (flushing) return flushing
  // The guard is cleared by the promise's own settlement, compared by
  // identity: an async body that returns before its first `await` (empty
  // box) settles synchronously, and a `finally` inside it would run BEFORE
  // this assignment — leaving a settled promise as the guard forever.
  const p = drainOutbox()
  flushing = p
  p.then(
    () => {
      if (flushing === p) flushing = null
    },
    () => {
      if (flushing === p) flushing = null
    },
  )
  return p
}

// Fire-and-forget: never throws, never blocks. The local mirror keeps the
// Events page meaningful in plain local dev where /api isn't real. `data` is
// an optional object of structured fields (the server stores it as-is, within
// its size limits); `actor` is used for the mirror only — the server stamps
// the actor from the verified token.
export function logEvent(type, { actor = '', actorName = '', detail = '', data = null } = {}) {
  if (!EVENT_TYPES[type]) return
  const payload = data && typeof data === 'object' && !Array.isArray(data) ? data : null
  const event = {
    id: `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    type,
    actor,
    actorName,
    detail,
    data: payload,
    createdAt: new Date().toISOString(),
  }
  try {
    writeLocal([event, ...readLocal()])
  } catch {
    /* ignore */
  }
  // Let live surfaces (the notifications bell, the events feed) pick this up
  // without waiting for a remount.
  try {
    window.dispatchEvent(new CustomEvent('revailosales:event', { detail: event }))
  } catch {
    /* SSR / no window */
  }
  // No Cognito = no token to verify = the server would refuse it. The mirror
  // is the whole record in that build, exactly as before.
  if (!isCognitoConfigured) return
  const entry = { id: event.id, type, actor_name: actorName, detail, data: payload, at: event.createdAt }
  // Older undelivered events go first. Posting a fresh event ahead of the
  // queue would put it before Friday's queued work in the server's CreatedAt
  // order; joining the queue keeps the wire order the real order, and the
  // drain that follows sends this one in its turn (or holds it, with the
  // rest, if the server is still not answering).
  if (readOutbox().length) {
    enqueue(entry)
    flushEventOutbox()
    return
  }
  ;(async () => {
    const token = await bearer()
    if (!token) {
      // Session lapsed (the refresh token is 30 days; the app's own session
      // never expires). Hold the event, and tell the app once.
      enqueue(entry)
      noteAuthFailure()
      return
    }
    // A live event is sent with its own `at` too, so the server-side row says
    // when it happened even if the POST was slow.
    const res = await postEvent(bodyFor(entry), token)
    if (res.ok) {
      noteAuthOk()
      // A success means the server is reachable and the token is good — the
      // best moment to drain whatever an earlier outage left behind.
      if (readOutbox().length) flushEventOutbox()
      return
    }
    if (res.retry) {
      enqueue(entry)
      if (res.status === 401) noteAuthFailure()
    }
    // Any other 4xx (400 unknown type, 403 not a member): dropped from the
    // wire — a retry cannot change the answer. The mirror still has it.
  })().catch(() => {})
}

// ── outbox triggers ──────────────────────────────────────────────────────────
// On load (after the current tick so the app has mounted and any sign-in
// redirect has settled), whenever the browser comes back online, and on the
// app's own session signal. auth.jsx also calls flushEventOutbox() directly
// after a sign-in, which is the case that matters most: the previous session
// lapsed, events queued, the rep signs back in, the queue drains.
try {
  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('online', () => {
      flushEventOutbox()
    })
    window.addEventListener('revailosales:session', () => {
      flushEventOutbox()
    })
    setTimeout(() => {
      flushEventOutbox()
    }, 0)
  }
} catch {
  /* non-browser context */
}

// ── shared time helpers (Events feed + Activity stats) ───────────────────────
// Absolute timestamp for audit surfaces: "Sep 24, 2026 2:36 AM".
export function absoluteTime(iso) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  return `${date} ${time}`
}

export function relativeTime(iso) {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const sec = Math.round((Date.now() - then) / 1000)
  if (sec < 60) return 'just now'
  const min = Math.round(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.round(hr / 24)
  if (day < 7) return `${day}d ago`
  return new Date(iso).toLocaleDateString()
}

export function daysAgo(iso) {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return Infinity
  return (Date.now() - then) / 86400000
}
